import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Products", path: "/products" },
  { name: "Why GFRP", path: "/why-gfrp" },
  { name: "Applications", path: "/applications" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 nav-glass" data-testid="navbar">
      <div className="section-container">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center">
              <span className="font-bold text-slate-900 text-xl font-['Barlow_Condensed']">BE</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-slate-900 text-lg font-['Barlow_Condensed'] tracking-tight">
                BUILDEDGE TECH
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-yellow-400 ${
                  isActive(link.path) 
                    ? "text-yellow-400 font-semibold" 
                    : "text-slate-700"
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button & Phone */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="tel:+919871070356" 
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+91-9871070356</span>
            </a>
            <Link to="/contact">
              <Button 
                className="bg-yellow-400 text-white hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none"
                data-testid="get-quote-btn"
              >
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-900" />
            ) : (
              <Menu className="w-6 h-6 text-slate-900" />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-6 mobile-menu-enter" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base font-medium py-2 border-b border-slate-100 ${
                    isActive(link.path) 
                      ? "text-yellow-400" 
                      : "text-slate-700"
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <a 
                href="tel:+919871070356" 
                className="flex items-center gap-2 text-slate-600 py-2"
              >
                <Phone className="w-4 h-4" />
                <span className="font-medium">+91-9871070356</span>
              </a>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                <Button 
                  className="w-full bg-yellow-400 text-white hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none"
                >
                  Get Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
